import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { analyzePasswordStrength } from "@/utils/securityUtils";
import { supabase } from "@/integrations/supabase/client";
import { parseRateLimitError, rateLimitStorage } from '@/utils/rateLimitUtils';
import { RateLimitCountdown } from '@/components/ui/RateLimitCountdown';
import BuyerPersonalInfoSection from "./BuyerPersonalInfoSection";
import BuyerInvestmentPreferencesSection from "./BuyerInvestmentPreferencesSection";
import { Mail, Loader2 } from "lucide-react";
import { safeStorage } from "@/utils/safariStorage";
import { trackLead } from "@/lib/facebookPixel";

type Step = 'info' | 'preferences' | 'emailVerification';

interface EmailValidationState {
  isValid: boolean;
  status: 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role';
  hasBeenValidated: boolean;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  companyName: string;
  phoneNumber: string;
  dealsAnnually: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  preferredMarkets: string[];
  purchasingType: string[];
  averageCloseTime: string;
  agreeTerms: boolean;
  smsOptIn?: boolean;
}

const STORAGE_KEY = 'buyer_registration_data';

const EnhancedBuyerRegistrationForm = () => {
  const [currentStep, setCurrentStep] = useState<Step>('info');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    type: 'ip' | 'email' | 'unknown';
    seconds: number;
  } | null>(null);
  const [isResuming, setIsResuming] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('resuming') === 'true' && urlParams.get('step') === '4';
  });
  const [emailValidation, setEmailValidation] = useState<EmailValidationState>({
    isValid: false,
    status: 'idle',
    hasBeenValidated: false
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { secureSignUp } = useSecureAuth();

  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    companyName: "",
    phoneNumber: "",
    dealsAnnually: 0,
    address: "",
    city: "",
    state: "",
    zipCode: "",
    preferredMarkets: [],
    purchasingType: [],
    averageCloseTime: "",
    agreeTerms: false,
    smsOptIn: false,
  });

  const passwordStrength = formData.password 
    ? analyzePasswordStrength(formData.password, {
        name: formData.fullName,
        email: formData.email,
        company: formData.companyName
      })
    : null;

  const steps = [
    { id: 'info' as Step, title: 'Account Info', number: 1 },
    { id: 'preferences' as Step, title: 'Investment Profile', number: 2 },
    { id: 'emailVerification' as Step, title: 'Email Verification', number: 3 },
  ];

  // Check for existing rate limit on mount
  useEffect(() => {
    const stored = rateLimitStorage.getRateLimitInfo('signup');
    if (stored.isRateLimited) {
      setRateLimitInfo({ type: 'ip', seconds: stored.remainingSeconds });
    }
  }, []);

  // Handle resume from email confirmation with onAuthStateChange listener
  useEffect(() => {
    const resuming = searchParams.get('resuming');
    const step = searchParams.get('step');
    
    if (resuming !== 'true' || step !== '4') {
      // Handle existing session without URL params (same device, returned later)
      const checkExistingSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const savedData = safeStorage.getItem(STORAGE_KEY);
          const emailVerificationPending = safeStorage.getItem('buyer_email_verification_pending');
          
          if (savedData && emailVerificationPending === 'true') {
            try {
            const parsed = JSON.parse(savedData);
              if (session.user.email === parsed.email) {
                setFormData(parsed);
                safeStorage.removeItem('buyer_email_verification_pending');
                handleRegistrationComplete();
              }
            } catch (error) {
              console.error('Failed to parse saved registration data:', error);
            }
          }
        }
      };
      checkExistingSession();
      return;
    }

    let isSubscribed = true;
    let sessionHandled = false;

    // Check for error in URL hash (Supabase error format)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const errorDescription = hashParams.get('error_description');
    if (errorDescription) {
      toast({
        title: "Verification Failed",
        description: errorDescription,
        variant: "destructive",
      });
      window.history.replaceState({}, '', '/buyer/register');
      setIsResuming(false);
      return;
    }

    // Function to handle session establishment
    const handleSessionEstablished = async (session: any) => {
      if (!isSubscribed || sessionHandled) return;
      sessionHandled = true;

      // First try localStorage (same device)
      const savedData = safeStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData(parsed);
          safeStorage.removeItem('buyer_email_verification_pending');
          window.history.replaceState({}, '', '/buyer/register');
          setIsResuming(false);
          handleRegistrationComplete();
          return;
        } catch (error) {
          console.error('Failed to parse saved registration data:', error);
        }
      }

      // Database fallback for cross-device scenario
      try {
        const { data: buyerProfile, error } = await supabase
          .from('buyer_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        
        if (error) throw error;
        
        if (buyerProfile) {
          const profileData = buyerProfile as any;
          const preferredMarkets = profileData.preferred_markets 
            ? (Array.isArray(profileData.preferred_markets) 
                ? profileData.preferred_markets as string[]
                : []) 
            : [];
          const purchasingType = profileData.purchasing_type 
            ? (Array.isArray(profileData.purchasing_type) 
                ? profileData.purchasing_type as string[]
                : []) 
            : [];
          
          setFormData({
            email: session.user.email || '',
            password: '',
            confirmPassword: '',
            fullName: profileData.full_name || '',
            companyName: profileData.company_name || '',
            phoneNumber: profileData.phone_number || '',
            dealsAnnually: profileData.deals_annually || 0,
            address: profileData.address || '',
            city: profileData.city || '',
            state: profileData.state || '',
            zipCode: profileData.zip_code || '',
            preferredMarkets,
            purchasingType,
            averageCloseTime: profileData.average_close_time || '',
            agreeTerms: true,
            smsOptIn: profileData.sms_opt_in || false,
          });
          
          safeStorage.removeItem('buyer_email_verification_pending');
          window.history.replaceState({}, '', '/buyer/register');
          setIsResuming(false);
          handleRegistrationComplete();
        }
      } catch (error) {
        console.error('Error fetching buyer profile from database:', error);
        toast({
          title: "Unable to Resume Registration",
          description: "Please start registration again or contact support.",
          variant: "destructive",
        });
        window.history.replaceState({}, '', '/buyer/register');
        setIsResuming(false);
      }
    };

    // Listen for auth state changes (handles URL token processing)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          await handleSessionEstablished(session);
        }
      }
    );

    // Also check immediately for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleSessionEstablished(session);
      }
    });

    // Timeout fallback - show error if no session after 8 seconds
    const timeoutId = setTimeout(() => {
      if (!sessionHandled && isSubscribed) {
        toast({
          title: "Verification Link Expired",
          description: "Please request a new verification email or start registration again.",
          variant: "destructive",
        });
        window.history.replaceState({}, '', '/buyer/register');
        setIsResuming(false);
      }
    }, 8000);

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [searchParams]);

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const saveToLocalStorage = () => {
    safeStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  };

  const clearLocalStorage = () => {
    safeStorage.removeItem(STORAGE_KEY);
  };

  const handleInfoStep = () => {
    // Check email validation status first
    if (emailValidation.hasBeenValidated && !emailValidation.isValid) {
      toast({
        title: "Email Not Available",
        description: emailValidation.status === 'wrong-role' 
          ? "This email is already registered with a different account type. Please use a different email."
          : "Please use a valid, unregistered email address.",
        variant: "destructive"
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    // Validate personal info
    if (!formData.email || !formData.password || !formData.fullName || !formData.phoneNumber) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords don't match",
        variant: "destructive"
      });
      return;
    }

    if (!passwordStrength?.isValid) {
      toast({
        title: "Weak Password",
        description: "Please create a stronger password",
        variant: "destructive"
      });
      return;
    }

    if (!formData.agreeTerms) {
      toast({
        title: "Terms Required",
        description: "You must agree to the Terms & Conditions",
        variant: "destructive"
      });
      return;
    }

    setCurrentStep('preferences');
  };

  const handlePreferencesStep = async () => {
    // GUARD: Never attempt signup if we're in resuming mode (stale link on new device)
    const resuming = searchParams.get('resuming');
    if (resuming === 'true') {
      toast({
        title: "Session Expired",
        description: "Your registration session has expired. Please start again.",
        variant: "destructive"
      });
      window.location.href = '/buyer/register';
      return;
    }

    // Check for existing session before attempting signup
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      navigate('/buyer/dashboard');
      return;
    }

    // Validate investment preferences
    if (formData.preferredMarkets.length === 0) {
      toast({
        title: "Missing Markets",
        description: "Please select at least one preferred market",
        variant: "destructive"
      });
      return;
    }

    if (formData.purchasingType.length === 0) {
      toast({
        title: "Missing Purchasing Type",
        description: "Please select at least one purchasing type",
        variant: "destructive"
      });
      return;
    }

    if (!formData.averageCloseTime) {
      toast({
        title: "Missing Close Time",
        description: "Please select your average close time",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Save data to localStorage before creating account
      saveToLocalStorage();

      // Create Supabase user account
      const result = await secureSignUp(
        formData.email,
        formData.password,
        {
          user_type: 'buyer',
          full_name: formData.fullName,
          company_name: formData.companyName || null,
          phone_number: formData.phoneNumber,
          deals_annually: formData.dealsAnnually,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zipCode || null,
          preferred_markets: formData.preferredMarkets,
          purchasing_type: formData.purchasingType,
          average_close_time: formData.averageCloseTime,
          sms_opt_in: formData.smsOptIn || false,
        },
        `https://godyspo.com/buyer/register?step=4&resuming=true`
      );

      if (result.error) {
        throw result.error;
      }

      // Send welcome email (non-blocking)
      try {
        const trialEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            userType: 'buyer',
            email: formData.email,
            fullName: formData.fullName,
            companyName: formData.companyName || undefined,
            trialEndDate: trialEndDate
          }
        });
        console.log('Welcome email sent successfully');
      } catch (emailError) {
        console.error('Welcome email failed to send:', emailError);
        // Don't block registration if email fails
      }

      // Clear any stale rate limit state since signup succeeded
      rateLimitStorage.clearRateLimit('signup');
      setRateLimitError(null);
      setRateLimitInfo(null);

      safeStorage.setItem('buyer_email_verification_pending', 'true');
      setCurrentStep('emailVerification');
      
      toast({
        title: "Email Sent!",
        description: "Please check your email to verify your account.",
      });
    } catch (error: any) {
      console.error('Buyer registration error:', {
        message: error?.message,
        status: error?.status,
        code: error?.code
      });
      
      const rateLimitResult = parseRateLimitError(error);
      
      if (rateLimitResult.isRateLimited) {
        const expiresAt = Date.now() + (rateLimitResult.retryAfterSeconds || 900) * 1000;
        rateLimitStorage.setRateLimited('signup', expiresAt);
        
        setRateLimitInfo({
          type: rateLimitResult.type,
          seconds: rateLimitResult.retryAfterSeconds || 900
        });
        setRateLimitError(rateLimitResult.message);
        
        toast({
          title: rateLimitResult.type === 'ip' ? "Server Rate Limit" : "Too Many Attempts",
          description: rateLimitResult.message,
          variant: "destructive",
        });
      } else if (error?.message?.includes('already registered') || error?.code === 'user_already_exists') {
        // Handle "account already exists" specifically
        toast({
          title: "Account Already Exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive"
        });
      } else {
        // Show user-friendly error for all other cases
        toast({
          title: "Registration Issue",
          description: error?.message || "Unable to create account. Please check your information and try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: {
          emailRedirectTo: `https://godyspo.com/buyer/register?step=4&resuming=true`,
        }
      });

      if (error) throw error;

      toast({
        title: "Email Resent",
        description: "Please check your inbox and spam folder.",
      });
      setRateLimitError(null);
    } catch (error: any) {
      const rateLimitResult = parseRateLimitError(error);
      
      if (rateLimitResult.isRateLimited) {
        const expiresAt = Date.now() + (rateLimitResult.retryAfterSeconds || 300) * 1000;
        rateLimitStorage.setRateLimited('signup', expiresAt);
        
        setRateLimitInfo({
          type: rateLimitResult.type,
          seconds: rateLimitResult.retryAfterSeconds || 300
        });
        setRateLimitError(rateLimitResult.message);
        
        toast({
          title: rateLimitResult.type === 'ip' ? "Server Rate Limit" : "Too Many Attempts",
          description: rateLimitResult.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to resend email",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationComplete = async () => {
    try {
      // Track Lead event for Facebook Pixel
      trackLead('buyer', 'Buyer Registration Complete');
      
      // Mark pending registration as complete (fire-and-forget)
      supabase.functions.invoke('save-pending-registration', {
        body: { 
          email: formData.email, 
          user_type: 'buyer',
          mark_completed: true
        }
      }).catch(() => {});

      // Clear all registration state
      clearLocalStorage();
      safeStorage.removeItem('buyer_email_verification_pending');
      
      // Clear URL parameters to prevent resuming state
      window.history.replaceState({}, '', '/buyer/register');
      
      // Verify session exists before navigation
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('handleRegistrationComplete: No session found', sessionError);
        toast({
          title: "Session Error",
          description: "Please log in to access your dashboard.",
          variant: "destructive"
        });
        navigate('/buyer/login');
        return;
      }
      
      toast({
        title: "Registration Complete!",
        description: "Welcome to GoDyspo. Redirecting to your dashboard...",
      });
      
      // Navigate with fallback
      setTimeout(() => {
        try {
          navigate('/buyer/dashboard');
        } catch (navError) {
          console.error('handleRegistrationComplete: Navigation error, using fallback', navError);
          window.location.href = '/buyer/dashboard';
        }
      }, 1500);
    } catch (error) {
      console.error('handleRegistrationComplete: Unexpected error', error);
      // Fallback: redirect using window.location to avoid ErrorBoundary
      window.location.href = '/buyer/dashboard';
    }
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex > 0 && steps[currentIndex - 1].id !== 'emailVerification') {
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const handleStartFresh = () => {
    clearLocalStorage();
    safeStorage.removeItem('buyer_email_verification_pending');
    setRateLimitError(null);
    setRateLimitInfo(null);
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      fullName: "",
      companyName: "",
      phoneNumber: "",
      dealsAnnually: 0,
      address: "",
      city: "",
      state: "",
      zipCode: "",
      preferredMarkets: [],
      purchasingType: [],
      averageCloseTime: "",
      agreeTerms: false,
      smsOptIn: false,
    });
    setCurrentStep('info');
    window.history.replaceState({}, '', '/buyer/register');
  };

  return (
    <div className="w-full">
      {/* Rate Limit Error Alert with Start Fresh Button */}
      {rateLimitError && (currentStep === 'info' || currentStep === 'preferences') && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg">
          <p className="text-red-400 text-sm mb-3">{rateLimitError}</p>
          <Button
            onClick={handleStartFresh}
            variant="outline"
            size="sm"
            className="text-sm border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700] hover:text-[#080c14]"
          >
            Start Fresh with Different Email
          </Button>
        </div>
      )}

      {rateLimitInfo && (currentStep === 'info' || currentStep === 'preferences') && (
        <RateLimitCountdown
          initialSeconds={rateLimitInfo.seconds}
          type={rateLimitInfo.type}
          onExpire={() => {
            setRateLimitInfo(null);
            setRateLimitError(null);
            rateLimitStorage.clearRateLimit('signup');
          }}
        />
      )}

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`text-sm ${
                  index === currentStepIndex 
                    ? 'text-[#ffd700] font-semibold' 
                    : index < currentStepIndex
                    ? 'text-[#ffd700]'
                    : 'text-gray-400'
                }`}
              >
                {step.number}.{index === currentStepIndex ? ` ${step.title}` : ''}
              </div>
            ))}
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {/* Show loading state when resuming from email verification */}
        {isResuming && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Verifying your email...</p>
          </div>
        )}

        {!isResuming && currentStep === 'info' && (
          <BuyerPersonalInfoSection
            formData={formData}
            updateFormData={updateFormData}
            passwordStrength={passwordStrength}
            onEmailValidationChange={setEmailValidation}
          />
        )}

        {currentStep === 'preferences' && (
          <BuyerInvestmentPreferencesSection
            formData={formData}
            updateFormData={updateFormData}
          />
        )}

        {currentStep === 'emailVerification' && (
          <div className="text-center py-8">
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-[#ffd700]/20 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#ffd700]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-[#ffd700] mb-4">
              Check Your Email
            </h3>
            <p className="text-gray-300 mb-2">
              We've sent a verification email to:
            </p>
            <p className="text-white font-semibold mb-6">{formData.email}</p>
            <p className="text-gray-400 mb-8">
              Click the link in the email to verify your account and continue to plan selection.
            </p>
            <Button
              onClick={handleResendEmail}
              disabled={isLoading}
              variant="outline"
              className="border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700] hover:text-[#080c14]"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resend Email
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              Don't see the email? Check your spam folder.
            </p>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {!isResuming && currentStep !== 'emailVerification' && (
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            disabled={currentStepIndex === 0 || isLoading}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Back
          </Button>

          <Button
            onClick={currentStep === 'info' ? handleInfoStep : handlePreferencesStep}
            disabled={
              isLoading || 
              (currentStep === 'info' && !formData.agreeTerms) ||
              (currentStep === 'info' && emailValidation.hasBeenValidated && !emailValidation.isValid)
            }
            className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {currentStep === 'preferences' ? 'Create Account' : 'Continue'}
          </Button>
        </div>
      )}

      {/* Login Link */}
      {!isResuming && currentStep !== 'emailVerification' && (
        <div className="text-center mt-6">
          <span className="text-sm text-gray-400">
            Already have an account?{" "}
            <Link to="/buyer/login" className="text-[#ffd700] hover:text-[#e6c200]">
              Sign in
            </Link>
          </span>
        </div>
      )}

      {/* Seller Link */}
      {!isResuming && currentStep !== 'emailVerification' && (
        <div className="text-center mt-2">
          <span className="text-sm text-gray-400">
            Are you a seller?{" "}
            <Link to="/seller/register" className="text-[#ffd700] hover:text-[#e6c200]">
              Register here
            </Link>
          </span>
        </div>
      )}
    </div>
  );
};

export default EnhancedBuyerRegistrationForm;